import mobileCssUrl from './design-system/mobile.css?url';
import { PWA_THEME_COLOR } from './design-system/pwa';
import * as React from 'react';
import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
} from "react-router";
import { ClientProviders } from "./components/ClientProviders";
import { WorktreeFooter } from "./components/WorktreeFooter";
import { ErrorState } from "./uptk/core/components/ui/ErrorState";
import messages_en from './translations/en.json';
import type { LinksFunction } from "react-router";

// Import stylesheets
import indexCssUrl from './index.css?url';
import designTokensCssUrl from './design-system/tokens.css?url';
import designSemanticCssUrl from './design-system/semantic.css?url';
import designComponentsCssUrl from './design-system/components.css?url';
import ds0CssUrl from './design-system/ds-0/ds.css?url';
import vendorCssUrl from './styles/vendor.css?url';
import tailwindCssUrl from './styles/tailwind.css?url';
import agGridCssUrl from 'ag-grid-community/dist/styles/ag-grid.css?url';
import agThemeBalhamCssUrl from 'ag-grid-community/dist/styles/ag-theme-balham.css?url';
import agThemeBalhamDarkCssUrl from 'ag-grid-community/dist/styles/ag-theme-balham-dark.css?url';
import agGridPortalCssUrl from './styles/ag-grid-portal.css?url';
// Font Awesome : couche `vendor` via app/styles/vendor.css (08.10), jamais un <link> hors couche.
// UPT-2049 : feuille d'impression des fiches, chargée en dernier (elle surcharge les jetons en @media print).
import printCssUrl from './design-system/print.css?url'; import geodeCssUrl from './design-system/geode.css?url';

export const links: LinksFunction = () => [
  { rel: 'stylesheet', href: indexCssUrl },
  { rel: 'stylesheet', href: mobileCssUrl },
  { rel: 'stylesheet', href: designTokensCssUrl },
  { rel: 'stylesheet', href: designSemanticCssUrl },
  { rel: 'stylesheet', href: designComponentsCssUrl },
  { rel: 'stylesheet', href: ds0CssUrl },
  // Font Awesome dans la couche `vendor` (vendor.css, traitée par Vite : url() des polices réécrites), avant Tailwind.
  { rel: 'stylesheet', href: vendorCssUrl },
  { rel: 'stylesheet', href: tailwindCssUrl },
  { rel: 'stylesheet', href: agGridCssUrl },
  { rel: 'stylesheet', href: agThemeBalhamCssUrl },
  { rel: 'stylesheet', href: agThemeBalhamDarkCssUrl },
  { rel: 'stylesheet', href: agGridPortalCssUrl },
  { rel: 'stylesheet', href: geodeCssUrl }, { rel: 'stylesheet', href: printCssUrl },
];



/**
 * UPT-1615 diagnostic — iframe/download tracer, enabled ONLY via
 * ?debugiframes=1. Wraps createElement to log every iframe's src (and every
 * programmatic <a download> click) into a visible on-page banner, so a mobile
 * user without devtools can report which third party triggers the iOS
 * "iframe.html" download prompt. Remove once UPT-1615 is closed.
 */


export function Layout({ children }: Readonly<{ children: React.ReactNode }>) {
  return (
    <html lang="en" className="dark" suppressHydrationWarning>
      <head>
        <meta charSet="utf-8" />
        <meta httpEquiv="X-UA-Compatible" content="IE=Edge" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        {/* UPT-1802: PWA brand color and icons, before theme initialization. */}
        <meta name="theme-color" content={PWA_THEME_COLOR} />
        <link rel="icon" href="/uptk-favicon.ico" sizes="48x48 32x32 16x16" />
        <link rel="icon" type="image/svg+xml" href="/uptk-favicon.svg" />
        <link rel="apple-touch-icon" href="/uptk-apple-touch-icon.png" sizes="180x180" />
        <link rel="manifest" id="manifest-placeholder" href="/manifest.json" />
        {/* eslint-disable-next-line i18next/no-literal-string -- i18n-exempt: document title / brand name */}
        <title id="title">Géode</title>
        <script src="/scripts/theme-bootstrap.js" />
        <script src="/scripts/iframe-tracer.js" />
        <Meta />
        <Links />
      </head>
      <body suppressHydrationWarning className="has-worktree-footer">
        {children}
        <WorktreeFooter />
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export function HydrateFallback() {
  // Rendered during hydration, BEFORE <IntlProvider> is mounted (react-intl's
  // useIntl() would throw "Could not find required `intl` object" here, which
  // also breaks the SPA prerender build), so copy is read straight from the
  // default (en) catalog rather than useIntl() — same rationale as ErrorBoundary.
  const t = (id: string) => (messages_en as Record<string, string>)[id] ?? id;
  return (
    <div className="flex min-h-screen items-center justify-center bg-portal-bg px-6 text-portal-ink transition-colors">
      <div className="ui-card w-full max-w-md p-6">
        <div className="flex items-center gap-4">
          <div className="h-10 w-10 animate-spin rounded-full border-2 border-portal-border border-t-portal-accent" />
          <div>
            <h2 className="ui-title text-lg font-semibold text-portal-ink">{t('app.root.loadingTitle')}</h2>
            <p className="mt-1 text-sm text-portal-ink-muted">{t('app.root.loadingSubtitle')}</p>
          </div>
        </div>
      </div>
    </div>
  )
}

export function ErrorBoundary({ error }: Readonly<{ error: Error }>) {
  // Build the raw diagnostic string OUTSIDE JSX: rendering error.message/.stack
  // inside any JSX container re-trips i18n Rule 4a. ErrorState only surfaces this
  // `detail` in DEV builds, so production users never see raw error text.
  const detail = [error?.message, error?.stack].filter(Boolean).join('\n\n');
  return (
    <html lang="en" className="dark" suppressHydrationWarning>
      <head>
        {/* eslint-disable-next-line i18next/no-literal-string -- i18n-exempt: document title / brand name */}
        <title>Error - Géode</title>
        <script src="/scripts/theme-bootstrap.js" />
        <Meta />
        <Links />
      </head>
      <body className="bg-portal-bg text-portal-ink transition-colors">
        <div className="flex min-h-screen items-center justify-center px-6 py-10">
          <div className="ui-form w-full max-w-3xl p-6">
            {/*
              Last-resort crash boundary: it renders its own <html> and runs even
              when the IntlProvider failed, so copy is read straight from the
              default (en) catalog rather than useIntl(). The raw error.message /
              error.stack are passed as `detail`, which ErrorState shows only in
              DEV builds — never to production users (i18n Rule 4a, UPT-183).
            */}
            <ErrorState
              title={(messages_en as Record<string, string>)['error.app.title']}
              description={(messages_en as Record<string, string>)['error.app.body']}
              retryLabel={(messages_en as Record<string, string>)['error.app.reload']}
              onRetry={() => window.location.reload()}
              detail={detail}
            />
          </div>
        </div>
        <Scripts />
      </body>
    </html>
  );
}

export default function Root() {
  return (
    <ClientProviders>
      <React.Suspense fallback={null}>
        <Outlet />
      </React.Suspense>
    </ClientProviders>
  );
}
